import { call, put, takeLatest } from 'redux-saga/effects';
import { PayloadAction } from '@reduxjs/toolkit';
import { CreateAccountPayload, AccountResponse } from '@models/User';
import { createAccountService } from '@/api/services/accountService';
import { configService } from '@/api/services/configService';
import { SagaIterator } from 'redux-saga';
import { APIResponse } from '@models/Invoice';
import { handleApiError } from '@/api/network/httpClient';
import { ConfigResponse } from '@models/Config';
import {
  createAccountStart,
  createAccountSuccess,
  createAccountFailure,
  fetchConfigFailure,
  fetchConfigStart,
  fetchConfigSuccess,
  fetchUserDetailFailure,
  fetchUserDetailSuccess,
  fetchUserDetailStart,
} from './accountSlice';

function* handleCreateAccount(action: PayloadAction<CreateAccountPayload>): SagaIterator {
  try {
    const response: APIResponse<AccountResponse> = (yield call(
      createAccountService.register,
      action.payload
    )) as APIResponse<AccountResponse>;

    yield put(createAccountSuccess(response));
  } catch (error: unknown) {
    const apiError = handleApiError(error);
    yield put(createAccountFailure(apiError.message || 'Failed to create user'));
  }
}

function* getUserDetails(): SagaIterator {
  try {
    const response = (yield call(
      createAccountService.getUserDetails
    )) as APIResponse<AccountResponse>;

    yield put(fetchUserDetailSuccess(response));
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Account creation failed';
    yield put(fetchUserDetailFailure(errorMessage));
  }
}

function* getConfig(): SagaIterator {
  try {
    const response = (yield call(configService.getConfig)) as APIResponse<ConfigResponse>;
    yield put(fetchConfigSuccess(response));
  } catch (error: unknown) {
    const errorMessage = error instanceof Error ? error.message : 'Failed to fetch config';
    yield put(fetchConfigFailure(errorMessage));
  }
}

export function* watchAccountSaga(): SagaIterator {
  yield takeLatest(createAccountStart.type, handleCreateAccount);
  yield takeLatest(fetchUserDetailStart.type, getUserDetails);
  yield takeLatest(fetchConfigStart.type, getConfig);
}
